<template>
  <div class="full-height" :class="isEcomV2 || isSpotlight ? 'container-wrapper' : ''">
    <MobialisFormWithValidation
      @onValidate="submit"
      novalidate
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <h2 class="pt-2">{{ $t('message.selectDealership.heading') }}</h2>
      <p class="lead mt-3 mb-4">{{ $t('message.selectDealership.msg') }}</p>
      <div class="row">
        <div class="col-sm-4">
          <mobialis-text-input-with-validation
            rules="length:6"
            :label="$t('message.selectDealership.postalCode')"
            v-model="postalCode"
            vid="postalCode"
            mask-type="postalCode"
            data-cy="postalCode"
            @change="getDealerships"
          ></mobialis-text-input-with-validation>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6">
          <mobialis-selection-input-with-validation
            rules="required"
            :label="$t('message.selectDealership.preferredDealership')"
            v-model="selectedDealership"
            :helper="helperText"
            :placeHolder="$t('message.selectDealership.selectDealership')"
            vid="dealerships"
            :options="formattedDealerships"
            :disabled="dealershipsLoading"
            data-cy="dealerships"
          ></mobialis-selection-input-with-validation>
        </div>
      </div>
      <div class="push-down-desktop">
        <mobialis-button type="submit" class="mt-5 mb-3" data-cy="continue">{{
          $t('message.common.continue')
        }}</mobialis-button>
      </div>
    </MobialisFormWithValidation>
  </div>
</template>

<script>
import cloneDeep from 'lodash/fp/cloneDeep';
import { mapGetters } from 'vuex';
import log from '@/services/log';
import { AVRProducts } from '@/utils/enums';

export default {
  name: 'SelectDealership',
  data() {
    return {
      postalCode: cloneDeep(this.$store.state.user.postalCode),
      selectedDealership: cloneDeep(this.$store.state.business.selectedDealership),
      helperText: this.$t('message.selectDealership.preferredDealershipHelper'),
      dealershipsLoading: false,
    };
  },
  created() {
    if (!this.$store.state.business.dealerships || this.$store.state.business.dealerships.length === 0) {
      this.init();
    }
  },
  computed: {
    ...mapGetters(['getProductHomeRoute', 'getCurrentProduct']),
    formattedDealerships() {
      const { dealerships } = this.$store.state.business;
      if (!dealerships) return [];
      const sortedDealerships = [...dealerships].sort((a, b) => {
        if (a.distanceInKm != null && b.distanceInKm != null) {
          if (a.distanceInKm !== b.distanceInKm) {
            return a.distanceInKm - b.distanceInKm;
          }
          return a.businessName.localeCompare(b.businessName);
        }
        if (a.distanceInKm != null) return -1;
        if (b.distanceInKm != null) return 1;
        return a.businessName.localeCompare(b.businessName);
      });
      return sortedDealerships.map(dealership => ({
        value: dealership.apiKey,
        externalId: dealership.externalId,
        label: `${dealership.businessName}${
          dealership.distanceInKm ? `  (${dealership.distanceInKm} ${this.$t('message.selectDealership.km')})` : ''
        }`,
      }));
    },
    formattedPostalCode() {
      if (typeof this.postalCode === 'string' && this.postalCode.length === 6) {
        return `${this.postalCode.slice(0, 3).toUpperCase()} ${this.postalCode.slice(3).toUpperCase()}`;
      }
      return null;
    },
    isEcomV2() {
      return this.getCurrentProduct === AVRProducts.ECOMV2;
    },
    isSpotlight() {
      return this.getCurrentProduct === AVRProducts.SPOTLIGHT;
    },
  },
  watch: {
    postalCode() {
      this.init();
    },
  },
  methods: {
    init() {
      if (this.postalCode.length === 6) {
        this.getDealershipsByPostalCode();
      } else if (this.postalCode.length === 0) {
        this.getDealerships();
      }
    },
    async getDealerships() {
      try {
        this.dealershipsLoading = true;
        await this.$store.dispatch('business/getDealerships', { postalCode: null, product: this.getCurrentProduct });
        if (this.postalCode) {
          this.helperText = this.$t('message.selectDealership.preferredDealershipHelperError', {
            postalCode: this.formattedPostalCode,
          });
        } else {
          this.helperText = this.$t('message.selectDealership.preferredDealershipHelper');
        }
        this.dealershipsLoading = false;
        this.selectedDealership = ''; // Reset selected dealership
      } catch (error) {
        this.$log('Error fetching dealerships', error);
        this.$router.push('/error');
      }
    },
    async getDealershipsByPostalCode() {
      try {
        this.dealershipsLoading = true;
        await this.$store.dispatch('business/getDealerships', {
          postalCode: this.postalCode,
          product: this.getCurrentProduct,
        });
        this.helperText = this.$t('message.selectDealership.preferredDealershipHelperSuccess', {
          postalCode: this.formattedPostalCode,
        });
        this.selectedDealership = '';
        this.dealershipsLoading = false;
      } catch (error) {
        this.$log('Error fetching dealerships for postal code', error);
        await this.getDealerships();
      }
    },
    async getSelectedDealershipObject() {
      const selectedDealershipObject = this.formattedDealerships.find(
        dealership => dealership.value === this.selectedDealership
      );
      if (selectedDealershipObject) {
        const currentQuery = { ...this.$route.query };

        // Replace apiKey and externalId in the query string
        const newQuery = {
          ...currentQuery,
          key: selectedDealershipObject.value,
          external_id: selectedDealershipObject.externalId,
        };

        // Update route
        this.$router.replace({
          path: this.$route.path,
          query: newQuery,
        });
        try {
          await this.$store.dispatch('business/getInfo', { route: this.$route });
        } catch (error) {
          log('Get business info error after selected dealership', error, {
            key: this.$route.query.key,
            external_id: this.$route.query.external_id || null,
          });
          this.goToErrorPage();
        }
      }
    },
    async submit(isValid) {
      if (!isValid) {
        return;
      }
      this.$store.commit('user/SET_POSTAL_CODE', this.postalCode);
      this.$store.commit('business/SET_SELECTED_DEALERSHIP', this.selectedDealership);
      await this.getSelectedDealershipObject();
      const routePath = this.getProductHomeRoute(this.getCurrentProduct) || this.homeRoute;
      const { year, make, model, trim, style } = this.$route.params;
      let nextPath = routePath;

      if (year && make && model) {
        if (trim && style) {
          // check if trim, style,  are defined in route  so open personal-info-form
          nextPath = 'personal-info-form';
        } else {
          // check if year, make, and model are defined so open details form
          nextPath = 'carfax-details-form';
        }
      }
      this.$router.push({ path: nextPath });
    },
  },
};
</script>

<style scoped lang="scss">
.full-height {
  min-height: calc(100vh - 210px);
  position: relative;
  padding-bottom: 120px;
}
.push-down-desktop {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
}
</style>
